import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Zap } from "lucide-react";

const links = [
  { label: "Funding Solutions", href: "#products" },
  { label: "Industries", href: "#industries" },
  { label: "How It Works", href: "#how-it-works" },
  { label: "Calculator", href: "#calculator" },
  { label: "About", href: "#why" },
];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass-strong border-b border-white/5" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        {/* Logo */}
        <a href="#top" className="flex items-center gap-3 group" data-testid="nav-logo">
          <div className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-[#0052FF] to-[#00FFA3] grid place-items-center overflow-hidden">
            <Zap className="w-5 h-5 text-black" strokeWidth={3} />
            <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
          </div>
          <div className="flex flex-col leading-none">
            <span className="font-display font-extrabold text-base tracking-tight">POWER FORWARD</span>
            <span className="text-[10px] tracking-[0.3em] text-[#00FFA3] font-mono">FUNDING</span>
          </div>
        </a>

        {/* Desktop links */}
        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.label}
              href={l.href}
              className="text-sm font-medium text-zinc-400 hover:text-white transition-colors relative group"
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {l.label}
              <span className="absolute left-0 -bottom-1 h-px w-0 group-hover:w-full bg-gradient-to-r from-[#0052FF] to-[#00FFA3] transition-all duration-300" />
            </a>
          ))}
        </nav>

        {/* CTA */}
        <div className="hidden md:flex items-center gap-3">
          <Link
            to="/portal/login"
            data-testid="nav-portal-login"
            className="text-sm font-medium text-zinc-400 hover:text-white transition-colors"
          >
            Client Portal
          </Link>
          <a
            href="tel:+19086767979"
            data-testid="nav-phone-cta"
            className="text-sm font-mono tracking-tight text-zinc-400 hover:text-white transition-colors hidden lg:inline"
          >
            +1 (908) 676-7979
          </a>
          <Link
            to="/application"
            data-testid="nav-apply-cta"
            className="relative inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#00FFA3] text-black font-semibold text-sm hover:bg-white transition-colors group"
          >
            Apply Now
            <span className="w-1.5 h-1.5 rounded-full bg-black animate-pulse" />
          </Link>
        </div>

        {/* Mobile toggle */}
        <div className="lg:hidden flex items-center gap-1">
          <button
            onClick={() => setOpen(!open)}
            className="text-white p-2"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden glass-strong border-t border-white/5 overflow-hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {links.map((l) => (
                <a
                  key={l.label}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="text-zinc-300 hover:text-[#00FFA3] transition-colors font-medium"
                >
                  {l.label}
                </a>
              ))}
              <Link
                to="/portal/login"
                onClick={() => setOpen(false)}
                className="text-zinc-300 hover:text-[#00FFA3] transition-colors font-medium"
                data-testid="nav-mobile-portal"
              >
                Client Portal
              </Link>
              <Link
                to="/application"
                onClick={() => setOpen(false)}
                className="mt-2 px-5 py-3 rounded-full bg-[#00FFA3] text-black font-semibold text-center"
                data-testid="nav-mobile-apply"
              >
                Apply Now
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
